import { z } from 'zod';

/*
 * Properties (inventory): what the agent agrees with the owner when taking the
 * listing, with the public part (title, price, district, characteristics) clearly
 * separated from the private one (exact address, map point, owners, keys, notes).
 * Rules shared by the API and the web forms. Messages are shown to users (Spanish).
 */

/** Characteristics each type uses; the others are cleared when saving. */
export const PROPERTY_FIELDS = [
  'bedrooms',
  'bathrooms',
  'parkingSpaces',
  'builtArea',
  'lotArea',
  'levels',
  'floorNumber',
  'maintenanceFee',
  'lotFront',
  'lotDepth',
  'yearBuilt',
] as const;
export type PropertyField = (typeof PROPERTY_FIELDS)[number];

export const PROPERTY_TYPES = {
  house: {
    label: 'Casa',
    fields: [
      'bedrooms',
      'bathrooms',
      'parkingSpaces',
      'builtArea',
      'lotArea',
      'levels',
      'yearBuilt',
      'maintenanceFee',
    ],
  },
  apartment: {
    label: 'Apartamento',
    fields: [
      'bedrooms',
      'bathrooms',
      'parkingSpaces',
      'builtArea',
      'floorNumber',
      'maintenanceFee',
      'yearBuilt',
    ],
  },
  land: { label: 'Terreno', fields: ['lotArea', 'lotFront', 'lotDepth'] },
  commercial: {
    label: 'Local comercial',
    fields: ['bathrooms', 'parkingSpaces', 'builtArea', 'lotArea', 'levels', 'maintenanceFee'],
  },
  office: {
    label: 'Oficina',
    fields: ['bathrooms', 'parkingSpaces', 'builtArea', 'floorNumber', 'maintenanceFee'],
  },
  warehouse: {
    label: 'Bodega',
    fields: ['bathrooms', 'parkingSpaces', 'builtArea', 'lotArea'],
  },
} as const satisfies Record<string, { label: string; fields: readonly PropertyField[] }>;
export type PropertyType = keyof typeof PROPERTY_TYPES;

export const PROPERTY_FIELD_LABELS: Record<PropertyField, string> = {
  bedrooms: 'Habitaciones',
  bathrooms: 'Baños',
  parkingSpaces: 'Parqueos',
  builtArea: 'Área de construcción',
  lotArea: 'Área del terreno',
  levels: 'Niveles',
  floorNumber: 'Piso o nivel',
  maintenanceFee: 'Cuota de mantenimiento (USD al mes)',
  lotFront: 'Frente (m)',
  lotDepth: 'Fondo (m)',
  yearBuilt: 'Año de construcción',
};

export const OPERATIONS = {
  sale: 'Venta',
  rent: 'Alquiler',
  sale_rent: 'Venta y alquiler',
} as const;
export type Operation = keyof typeof OPERATIONS;

export const PROPERTY_STATUSES = {
  draft: 'Borrador',
  available: 'Disponible',
  reserved: 'Reservada',
  sold: 'Vendida',
  rented: 'Alquilada',
  withdrawn: 'Retirada',
} as const;
export type PropertyStatus = keyof typeof PROPERTY_STATUSES;

/**
 * The property's location on the website's map (Google Maps, loaded on click):
 * approximate by default (the map searched by name, zone and district, with no
 * coordinates) or exact (a marker on the real point, and "Cómo llegar"). Without
 * coordinates there is no map.
 */
export const WEB_LOCATION_MODES = {
  approximate: 'Aproximada (zona y distrito, sin marcador)',
  exact: 'Exacta (marcador y «Cómo llegar»)',
} as const;
export type WebLocationMode = keyof typeof WEB_LOCATION_MODES;

/** m² or varas cuadradas (v², common for land in El Salvador; 1 v² = 0.698896 m²). */
export const AREA_UNITS = { m2: 'm²', v2: 'v²' } as const;
export type AreaUnit = keyof typeof AREA_UNITS;
export const SQUARE_VARA_IN_M2 = 0.698896;

/** An area in m² (to compare areas given in different units). */
export function areaInM2(value: number, unit: AreaUnit): number {
  return unit === 'v2' ? value * SQUARE_VARA_IN_M2 : value;
}

/** Days before the listing agreement ends when the agent is warned. */
export const LISTING_EXPIRY_NOTICE_DAYS = 30;

export const needsSalePrice = (op: Operation) => op === 'sale' || op === 'sale_rent';
export const needsRentPrice = (op: Operation) => op === 'rent' || op === 'sale_rent';

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Máximo ${String(max)} caracteres`)
    .transform((v) => (v === '' ? null : v))
    .nullable();

const httpsUrl = z
  .string()
  .trim()
  .max(500)
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .refine(
    (v) => v === null || /^https:\/\/[^\s]+\.[^\s]+$/.test(v),
    'Enlace no válido: debe empezar con https://',
  );

const money = z
  .number('Escribe un número')
  .positive('Debe ser mayor que cero')
  .max(999_999_999_999, 'Monto demasiado alto')
  .multipleOf(0.01, 'Máximo 2 decimales')
  .nullable();
const count = (max: number) => z.number().int('Número entero').min(0).max(max).nullable();
const measure = z.number().positive('Debe ser mayor que cero').max(9_999_999).nullable();
const percent = z.number().min(0).max(100, 'Máximo 100 %').nullable();

export const propertyOwnerSchema = z.strictObject({
  contactId: z.uuid('Contacto no válido'),
  /** Share of ownership; all or none of the owners have it, and then they add up to 100. */
  sharePct: z.number().gt(0, 'Mayor que 0').max(100).nullable().default(null),
});

/** Create and edit share the same body. */
export const propertySchema = z
  .strictObject({
    title: z.string().trim().min(1, 'El título es obligatorio').max(150),
    description: optionalText(10_000).default(null),
    /** For the English website (it lists only properties with an English title). */
    titleEn: optionalText(150).default(null),
    descriptionEn: optionalText(10_000).default(null),
    type: z.enum(Object.keys(PROPERTY_TYPES) as [PropertyType]),
    operation: z.enum(Object.keys(OPERATIONS) as [Operation]),
    salePrice: money.default(null),
    rentPrice: money.default(null),
    /** Primary location, shown and searched: the district (Santa Tecla...). */
    districtId: z.number('Elige el distrito').int().positive(),
    /** Zone or neighborhood, public ("Colonia Escalón"). */
    neighborhood: optionalText(120).default(null),
    /** Exact address and map point: private, encrypted, revealed on demand. undefined = keep. */
    address: optionalText(300).optional(),
    location: z
      .strictObject({ lat: z.number().min(12.9).max(14.5), lng: z.number().min(-90.2).max(-87.6) })
      .nullable()
      .optional(),
    cadastralCode: optionalText(60).default(null),
    /** Links only (YouTube, Vimeo, Matterport...): opened in a new tab, never embedded. */
    videoUrl: httpsUrl.default(null),
    virtualTourUrl: httpsUrl.default(null),

    bedrooms: count(50).default(null),
    bathrooms: z
      .number()
      .min(0)
      .max(50)
      .multipleOf(0.5, 'Usa medios baños (.5)')
      .nullable()
      .default(null),
    parkingSpaces: count(500).default(null),
    builtArea: measure.default(null),
    lotArea: measure.default(null),
    /** Land in v² and construction in m² is common: each area has its unit. */
    lotAreaUnit: z.enum(Object.keys(AREA_UNITS) as [AreaUnit]).default('m2'),
    webLocationMode: z
      .enum(Object.keys(WEB_LOCATION_MODES) as [WebLocationMode])
      .default('approximate'),
    builtAreaUnit: z.enum(Object.keys(AREA_UNITS) as [AreaUnit]).default('m2'),
    levels: count(200).default(null),
    floorNumber: z.number().int().min(-5).max(200).nullable().default(null),
    maintenanceFee: money.default(null),
    lotFront: measure.default(null),
    lotDepth: measure.default(null),
    yearBuilt: z.number().int().min(1800).max(2100).nullable().default(null),

    /** Required when creating; on edit, undefined keeps them (changing them needs sensitive.view). */
    owners: z
      .array(propertyOwnerSchema)
      .min(1, 'Agrega al menos un propietario')
      .max(10)
      .optional(),
    assignedMembershipId: z.uuid('Agente no válido').optional(),
    capturedByMembershipId: z.uuid('Captador no válido').optional(),
    exclusive: z.boolean().default(false),
    listingStartsOn: z.iso.date('Fecha no válida').nullable().default(null),
    listingEndsOn: z.iso.date('Fecha no válida').nullable().default(null),

    commissionSalePct: percent.default(null),
    commissionRentMonths: z.number().min(0).max(24).multipleOf(0.25).nullable().default(null),
    networkSharePct: percent.default(null),
    commissionConditions: optionalText(1000).default(null),

    keysCount: count(99).default(null),
    /** Private: encrypted. */
    keysLocation: optionalText(300).default(null),
    privateNotes: optionalText(5000).default(null),
  })
  .superRefine((v, ctx) => {
    const issue = (path: string, message: string) => {
      ctx.addIssue({ code: 'custom', path: [path], message });
    };
    if (v.salePrice !== null && !needsSalePrice(v.operation))
      issue('salePrice', 'Solo aplica si la operación incluye venta');
    if (v.rentPrice !== null && !needsRentPrice(v.operation))
      issue('rentPrice', 'Solo aplica si la operación incluye alquiler');
    if (v.exclusive && !v.listingEndsOn)
      issue('listingEndsOn', 'En una exclusiva indica cuándo vence el acuerdo');
    if (v.listingStartsOn && v.listingEndsOn && v.listingEndsOn < v.listingStartsOn)
      issue('listingEndsOn', 'Debe ser posterior al inicio');
    const owners = v.owners ?? [];
    const ids = owners.map((o) => o.contactId);
    if (new Set(ids).size !== ids.length) issue('owners', 'Un propietario aparece dos veces');
    const shares = owners.map((o) => o.sharePct);
    if (shares.some((s) => s !== null)) {
      if (shares.some((s) => s === null))
        issue('owners', 'Indica el % de todos los propietarios, o de ninguno');
      else if (Math.abs(shares.reduce<number>((a, s) => a + (s ?? 0), 0) - 100) > 0.001)
        issue('owners', 'Los porcentajes deben sumar 100 %');
    }
  });
export type PropertyInput = z.input<typeof propertySchema>;
export type PropertyData = z.output<typeof propertySchema>;

/** Characteristics a type does not use are dropped (a land has no bedrooms). */
export function propertyFieldApplies(type: PropertyType, field: PropertyField): boolean {
  return (PROPERTY_TYPES[type].fields as readonly PropertyField[]).includes(field);
}

export const propertyListQuerySchema = z.strictObject({
  /** Title, zone or code. */
  q: z.string().trim().max(100).optional(),
  type: z.enum(Object.keys(PROPERTY_TYPES) as [PropertyType]).optional(),
  operation: z.enum(['sale', 'rent']).optional(),
  status: z.enum(Object.keys(PROPERTY_STATUSES) as [PropertyStatus]).optional(),
  districtId: z.coerce.number().int().positive().optional(),
  municipalityId: z.coerce.number().int().positive().optional(),
  priceMin: z.coerce.number().min(0).optional(),
  priceMax: z.coerce.number().min(0).optional(),
  bedroomsMin: z.coerce.number().int().min(0).max(50).optional(),
  /** Areas compared in m² whatever each property's unit; `areaUnit` is the filter's. */
  lotAreaMin: z.coerce.number().min(0).optional(),
  lotAreaMax: z.coerce.number().min(0).optional(),
  builtAreaMin: z.coerce.number().min(0).optional(),
  builtAreaMax: z.coerce.number().min(0).optional(),
  areaUnit: z.enum(['m2', 'v2']).default('m2'),
  /** Imported without an owner (it cannot be published until one is added). */
  withoutOwner: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  assignedMembershipId: z.uuid().optional(),
  exclusive: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  /** Listing agreement ending within 30 days, or already ended. */
  listing: z.enum(['expiring', 'expired']).optional(),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type PropertyListQuery = z.infer<typeof propertyListQuerySchema>;

/** Does it already exist? Compared by exact address, owners and cadastral code. */
export const propertyDuplicateQuerySchema = z.strictObject({
  address: z.string().max(300).nullable().optional(),
  ownerIds: z.array(z.uuid()).max(10).default([]),
  cadastralCode: z.string().max(60).nullable().optional(),
  excludeId: z.uuid().optional(),
});
export type PropertyDuplicateQuery = z.input<typeof propertyDuplicateQuerySchema>;

export const propertyStatusChangeSchema = z.strictObject({
  status: z.enum(Object.keys(PROPERTY_STATUSES) as [PropertyStatus]),
  note: optionalText(500).optional(),
});
export type PropertyStatusChange = z.infer<typeof propertyStatusChangeSchema>;

export const webPublicationSchema = z.strictObject({
  published: z.boolean(),
  /** Featured on the website (home page carousel); only while published. */
  featured: z.boolean().optional(),
});

/** Statuses a property can be published on the website in. */
export const WEB_PUBLISHABLE_STATUSES: readonly PropertyStatus[] = ['available', 'reserved'];

/**
 * What a listing needs before going to the website or the Network (price, zone and
 * description; no minimum of photos) and what it lacks to be complete.
 */
export function propertyCompleteness(p: {
  type: PropertyType;
  operation: Operation;
  salePrice: number | null;
  rentPrice: number | null;
  description: string | null;
  districtId: number | null;
  bedrooms: number | null;
  builtArea: number | null;
  lotArea: number | null;
  ownersCount: number;
}): { publishable: boolean; percent: number; missing: string[]; blocking: string[] } {
  const required: [boolean, string][] = [
    [!needsSalePrice(p.operation) || p.salePrice !== null, 'Precio de venta'],
    [!needsRentPrice(p.operation) || p.rentPrice !== null, 'Precio de alquiler'],
    [p.districtId !== null, 'Zona (distrito)'],
    [(p.description?.length ?? 0) >= 50, 'Descripción (al menos 50 caracteres)'],
    // Imported listings may arrive without one; they are not published until it is added.
    [p.ownersCount > 0, 'Propietario'],
  ];
  const recommended: [boolean, string][] = [
    [!propertyFieldApplies(p.type, 'bedrooms') || p.bedrooms !== null, 'Habitaciones'],
    p.type === 'land'
      ? [p.lotArea !== null, 'Área del terreno']
      : [p.builtArea !== null, 'Área de construcción'],
  ];
  const all = [...required, ...recommended];
  return {
    publishable: required.every(([ok]) => ok),
    percent: Math.round((all.filter(([ok]) => ok).length / all.length) * 100),
    missing: all.filter(([ok]) => !ok).map(([, label]) => label),
    /** What prevents publishing. */
    blocking: required.filter(([ok]) => !ok).map(([, label]) => label),
  };
}

// ---------------------------------------------------------------------------
// Photos, plans and share links
// ---------------------------------------------------------------------------

export const MEDIA_KINDS = { photo: 'Foto', plan: 'Plano' } as const;
export type MediaKind = keyof typeof MEDIA_KINDS;

/** Uploads: JPEG, PNG or WebP up to 20 MB; at most 60 per property. */
export const MEDIA_MAX_BYTES = 20 * 1024 * 1024;
export const MEDIA_MAX_PER_PROPERTY = 60;
export const MEDIA_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

export const mediaUploadFieldsSchema = z.strictObject({
  kind: z.enum(Object.keys(MEDIA_KINDS) as [MediaKind]).default('photo'),
  isPrivate: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .default(false),
});

export const mediaUpdateSchema = z
  .strictObject({
    kind: z.enum(Object.keys(MEDIA_KINDS) as [MediaKind]).optional(),
    isPrivate: z.boolean().optional(),
    caption: optionalText(200).optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'No hay cambios');
export type MediaUpdateInput = z.infer<typeof mediaUpdateSchema>;

/** The new order: every current photo id, first = cover (the first public one is shown). */
export const mediaOrderSchema = z.strictObject({
  ids: z.array(z.uuid()).min(1).max(MEDIA_MAX_PER_PROPERTY),
});

/** Days a share link lasts by default, and at most. */
export const SHARE_LINK_DEFAULT_DAYS = 30;
export const SHARE_LINK_MAX_DAYS = 180;

export const shareLinkSchema = z.strictObject({
  days: z.number().int().min(1).max(SHARE_LINK_MAX_DAYS).default(SHARE_LINK_DEFAULT_DAYS),
});
export type ShareLinkInput = z.input<typeof shareLinkSchema>;
